import { Button, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import {
  PROOF_KIND,
  type ProofItem,
  type ProofKind,
  proofChecklistActions,
  proofChecklistQuery,
} from "../../deliveries";
import { messageFor } from "../../error-copy";
import { moved } from "../../fleet";
import { ConfirmDialog } from "../shared/confirm";
import styles from "./settings.module.css";

const KINDS = Object.keys(PROOF_KIND) as ProofKind[];

/**
 * Delivery proof (P8-49): what a driver answers in the driver app to complete a delivery, besides
 * who received it, in order. Drivers see a change on their next delivery; past proofs keep the
 * answers they gave.
 */
export function ProofChecklistPage(): ReactNode {
  const queryClient = useQueryClient();
  const checklist = useQuery(proofChecklistQuery);
  const [editing, setEditing] = useState<ProofItem | "new" | null>(null);
  const [removing, setRemoving] = useState<ProofItem | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: proofChecklistQuery.queryKey });
  const reorder = useMutation({ mutationFn: proofChecklistActions.reorder, onSuccess: refresh });

  const header = (
    <PageHeader
      title="Delivery proof"
      description="What a driver answers in the driver app to complete a delivery: photos, a signature, checks, readings and notes."
      actions={<Button onClick={() => setEditing("new")}>Add an item</Button>}
    />
  );
  if (checklist.status === "pending")
    return (
      <>
        {header}
        <PageLoading label="Loading the checklist" />
      </>
    );
  if (checklist.status === "error")
    return (
      <>
        {header}
        <PageError message={messageFor(errorOf(checklist.error))} onRetry={() => checklist.refetch()} />
      </>
    );
  const items = checklist.data.items;
  return (
    <>
      {header}
      <div className={styles.panel}>
        <p className={styles.muted}>Drivers always give the name of the person who received the delivery.</p>
        {reorder.error ? (
          <Alert tone="danger" title="Not saved">
            {messageFor(errorOf(reorder.error))}
          </Alert>
        ) : null}
        <SortableDataTable
          caption="Proof items, in the order drivers see them"
          rowKey="id"
          rows={items}
          itemName={{ one: "item", other: "items" }}
          emptyMessage="No items: drivers complete a delivery with the receiver's name only. Add items to ask for more."
          columns={[
            {
              key: "label",
              label: "Item",
              sortable: false,
              render: (_v, i) => (
                <span>
                  {i.label}
                  {i.help_text ? <span className={styles.muted}> · {i.help_text}</span> : null}
                </span>
              ),
            },
            { key: "kind", label: "Answer", sortable: false, render: (_v, i) => PROOF_KIND[i.kind] },
            {
              key: "required",
              label: "Required",
              sortable: false,
              render: (_v, i) =>
                i.required ? <Badge tone="info">Required</Badge> : <Badge tone="neutral">Optional</Badge>,
            },
            {
              key: "actions",
              label: "",
              sortable: false,
              render: (_v, i) => {
                const at = items.indexOf(i);
                return (
                  <div className={styles.actions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={at === 0 || reorder.isPending}
                      aria-label={`Move ${i.label} up`}
                      onClick={() => reorder.mutate(moved(items, i.id, -1))}
                    >
                      Up
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={at === items.length - 1 || reorder.isPending}
                      aria-label={`Move ${i.label} down`}
                      onClick={() => reorder.mutate(moved(items, i.id, 1))}
                    >
                      Down
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(i)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setRemoving(i)}>
                      Remove
                    </Button>
                  </div>
                );
              },
            },
          ]}
        />
        <p className={styles.muted}>
          A required yes-or-no item needs an answer, yes or no: a "no" is recorded, it doesn't stop the delivery. Staff
          marking a delivery delivered for a driver can leave out photos and signatures. Changes apply from the next
          delivery; past proofs keep the answers they gave.
        </p>
      </div>
      {editing ? <ItemDialog item={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} /> : null}
      {removing ? (
        <ConfirmDialog
          title={`Remove ${removing.label}?`}
          description="Drivers stop seeing it from their next delivery. Past proofs keep their answers."
          action="Remove"
          danger
          run={() => proofChecklistActions.archiveItem(removing.id)}
          onDone={refresh}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}

/** Add an item, or edit one. Once drivers have answered an item, its kind stays as it is. */
function ItemDialog({ item, onClose }: { item?: ProofItem; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(item?.label ?? "");
  const [help, setHelp] = useState(item?.help_text ?? "");
  const [kind, setKind] = useState<ProofKind>(item?.kind ?? "yes_no");
  const [required, setRequired] = useState(item?.required ?? true);
  const mutation = useMutation({
    mutationFn: () => {
      const body = { label: label.trim(), help_text: help.trim() || null, kind, required };
      return item ? proofChecklistActions.updateItem(item.id, body) : proofChecklistActions.createItem(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: proofChecklistQuery.queryKey });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={item ? `Edit ${item.label}` : "Add a proof item"}>
        <div className={styles.form}>
          <Input
            label="What to ask"
            value={label}
            maxLength={80}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Tank lid closed"
          />
          <Input
            label="Help for the driver"
            value={help}
            maxLength={300}
            onChange={(e) => setHelp(e.target.value)}
            placeholder="Check the lid is locked after filling"
            description="Optional."
          />
          <Select
            label="Answer"
            value={kind}
            onValueChange={(v) => setKind(v as ProofKind)}
            options={KINDS.map((k) => ({ value: k, label: PROOF_KIND[k] }))}
          />
          <Switch label="Required to complete a delivery" checked={required} onCheckedChange={setRequired} />
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!label.trim()} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {item ? "Save" : "Add item"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
